/// OpenRouter API client for streaming and non-streaming chat completions.
///
/// Uses the OpenAI-compatible endpoint at https://openrouter.ai/api/v1/chat/completions
/// with direct browser fetch (OpenRouter supports CORS). SSE parsing via eventsource-parser.

import { createParser, type EventSourceMessage } from "eventsource-parser";
import { OpenRouterError } from "@/types/ai";
import type { ChatMessage } from "@/types/ai";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_URL = "https://openrouter.ai/api/v1/chat/completions";

/** Standard headers for OpenRouter requests. */
function buildHeaders(apiKey: string): Record<string, string> {
  return {
    Authorization: `Bearer ${apiKey}`,
    "Content-Type": "application/json",
    "HTTP-Referer": typeof window !== "undefined" ? window.location.origin : "https://flowy.dev",
    "X-Title": "InovaBPM Workflow Editor",
  };
}

// ---------------------------------------------------------------------------
// Message formatting
// ---------------------------------------------------------------------------

/** Convert internal ChatMessage to OpenRouter API message format. */
function toAPIMessages(messages: ChatMessage[]): { role: string; content: string }[] {
  return messages.map((m) => ({ role: m.role, content: m.content }));
}

// ---------------------------------------------------------------------------
// Streaming chat completion
// ---------------------------------------------------------------------------

/**
 * Stream a chat completion from OpenRouter, yielding content delta strings.
 *
 * Uses fetch + ReadableStream + eventsource-parser callback pattern.
 * The async generator bridges the parser callback with the consumer by
 * accumulating deltas in a queue that the reader loop drains.
 */
export async function* streamChatCompletion(
  apiKey: string,
  model: string,
  messages: ChatMessage[],
  signal?: AbortSignal,
): AsyncGenerator<string, void, undefined> {
  const response = await fetch(API_URL, {
    method: "POST",
    headers: buildHeaders(apiKey),
    body: JSON.stringify({
      model,
      messages: toAPIMessages(messages),
      stream: true,
    }),
    signal,
  });

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = await response.text();
    }
    const code =
      typeof errorBody === "object" && errorBody !== null
        ? ((((errorBody as Record<string, unknown>).error as Record<string, unknown>)
            ?.code as string) ?? "unknown")
        : "unknown";
    throw new OpenRouterError(response.status, code, errorBody);
  }

  if (!response.body) {
    throw new OpenRouterError(0, "no_body", "Response body is null");
  }

  // Queue to bridge parser callbacks with async generator yield
  const contentQueue: string[] = [];
  let streamDone = false;
  let parserError: Error | null = null;

  const parser = createParser({
    onEvent(event: EventSourceMessage) {
      if (event.data === "[DONE]") {
        streamDone = true;
        return;
      }
      try {
        const parsed = JSON.parse(event.data) as {
          choices?: { delta?: { content?: string } }[];
        };
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) {
          contentQueue.push(content);
        }
      } catch (e) {
        parserError = e instanceof Error ? e : new Error(String(e));
      }
    },
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        parser.feed(decoder.decode(value, { stream: true }));
      }

      // Drain the content queue
      for (let item = contentQueue.shift(); item !== undefined; item = contentQueue.shift()) {
        yield item;
      }

      if (parserError) {
        throw parserError;
      }

      if (done || streamDone) {
        break;
      }
    }

    // Drain any remaining content after stream ends
    for (let item = contentQueue.shift(); item !== undefined; item = contentQueue.shift()) {
      yield item;
    }
  } finally {
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Connection test
// ---------------------------------------------------------------------------

/**
 * Test whether an API key is valid by making a minimal chat completion request.
 * Returns true if the key is accepted, false otherwise.
 */
export async function testConnection(apiKey: string): Promise<boolean> {
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: buildHeaders(apiKey),
      body: JSON.stringify({
        model: "openrouter/auto",
        messages: [{ role: "user", content: "Hello" }],
        max_tokens: 5,
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
}
